/**
 * Shared place card template loading and legacy field upgrades (used by the designer and attendance list).
 */

import { collection, getDocs } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import type {
  Event,
  FieldType,
  PlaceCardElement,
  PlaceCardField,
  PlaceCardFrame,
  PlaceCardPlaceholder,
  PlaceCardTemplate,
} from '@/types';

export const DEFAULT_PLACE_CARD_FRAME: PlaceCardFrame = {
  enabled: false,
  style: 'solid',
  thickness: 2,
  color: '#111827',
  padding: 12,
  offsetX: 0,
  offsetY: 0,
  cornerRadius: 0,
};

type FirestoreDateLike = Date | { toDate: () => Date } | null | undefined;

function resolveFirestoreDate(value: FirestoreDateLike, fallback?: Date): Date {
  if (value instanceof Date) {
    return value;
  }
  if (value && typeof (value as { toDate: () => Date }).toDate === 'function') {
    try {
      return (value as { toDate: () => Date }).toDate();
    } catch (error) {
      console.warn('Failed to convert Firestore timestamp', error);
    }
  }
  return fallback ?? new Date();
}

export function normalizePlaceCardTemplateDates<
  T extends { createdAt?: FirestoreDateLike; updatedAt?: FirestoreDateLike },
>(template: T): T & { createdAt: Date; updatedAt: Date } {
  return {
    ...template,
    createdAt: resolveFirestoreDate(template.createdAt),
    updatedAt: resolveFirestoreDate(template.updatedAt),
  };
}

const FIELD_PLACEHOLDER_MAP: Partial<Record<FieldType, PlaceCardPlaceholder>> = {
  eventName: '{event_name}',
  eventDate: '{event_date}',
  eventTitle: '{event_name}',
  fullName: '{guest_full_name}',
  firstName: '{guest_first_name}',
  lastName: '{guest_last_name}',
  rank: '{guest_rank}',
  rankAndName: '{guest_rank} {guest_full_name}',
  mealChoice: '{guest_meal}',
  tableName: '{guest_table}',
  seatNumber: '{guest_seat}',
  unitName: '{unit_name}',
  unitNumber: '{unit_number}',
};

function convertFieldToElement(field: PlaceCardField): PlaceCardElement {
  const basePlaceholder = FIELD_PLACEHOLDER_MAP[field.type];
  const isLogo = field.type === 'unitLogo';

  return {
    id: field.id,
    type: isLogo ? 'image' : 'text',
    name: field.label,
    x: field.x / 100,
    y: field.y / 100,
    width: field.width / 100,
    height: field.height / 100,
    rotation: 0,
    opacity: 1,
    content: isLogo
      ? undefined
      : field.type === 'customText'
        ? field.customValue ?? ''
        : basePlaceholder ?? field.label,
    fontSize: field.fontSize,
    fontWeight: field.fontWeight,
    textAlign: field.textAlign,
    color: field.color,
    mirrored: field.flipped ?? false,
    mirrorRotation: 180,
    imageType: isLogo ? 'unit-logo' : undefined,
    preserveAspectRatio: true,
  };
}

/** Legacy font values that depended on locally installed fonts, mapped to the loaded web font. */
const LEGACY_FONT_FAMILIES: Record<string, string> = {
  'Garamond, serif': 'EB Garamond, Garamond, serif',
};

function upgradeElementFont(element: PlaceCardElement): PlaceCardElement {
  const replacement = element.fontFamily ? LEGACY_FONT_FAMILIES[element.fontFamily] : undefined;
  return replacement ? { ...element, fontFamily: replacement } : element;
}

export function upgradePlaceCardTemplate(template: PlaceCardTemplate): PlaceCardTemplate {
  const elements =
    (!template.elements || template.elements.length === 0) && template.fields && template.fields.length > 0
      ? template.fields.map(convertFieldToElement)
      : template.elements;

  return {
    ...template,
    ...(elements ? { elements: elements.map(upgradeElementFont) } : {}),
    layout: template.layout ?? { foldLinePosition: 0.5, showFoldLine: true, mirrorByDefault: false },
  };
}

export function createDefaultPlaceCardTemplate(event: Event, unitId: string): PlaceCardTemplate {
  const now = new Date();
  return {
    id: '',
    unitId,
    eventId: event.id,
    name: `${event.name} Place Cards`,
    paperSize: 'A4',
    cardSize: '4x6',
    cardsPerPage: 2,
    customCardWidth: undefined,
    customCardHeight: undefined,
    elements: [],
    layout: { foldLinePosition: 0.5, showFoldLine: true, mirrorByDefault: false },
    createdAt: now,
    updatedAt: now,
  };
}

/**
 * Loads the default or most recently updated place card template for this event (same rules as the place card designer).
 */
export async function loadEventPlaceCardTemplate(unitId: string, event: Event): Promise<PlaceCardTemplate> {
  const templatesRef = collection(db, 'units', unitId, 'placeCardTemplates');
  const snapshot = await getDocs(templatesRef);
  const templates = snapshot.docs.map((docSnap) => {
    const data = docSnap.data() as PlaceCardTemplate & {
      createdAt?: FirestoreDateLike;
      updatedAt?: FirestoreDateLike;
    };
    return upgradePlaceCardTemplate(
      normalizePlaceCardTemplateDates({
        ...data,
        id: docSnap.id,
      })
    );
  });

  const forEvent = templates.filter((tpl) => tpl.eventId === event.id);
  const existing =
    forEvent.find((tpl) => tpl.isDefault === true) ??
    [...forEvent].sort((a, b) => b.updatedAt.getTime() - a.updatedAt.getTime())[0];

  return existing ?? createDefaultPlaceCardTemplate(event, unitId);
}
